import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAdminConsoleAccess } from '@/lib/auth/admin-console-access';
import { useWorkspaceApplications } from '@/lib/applications/workspace-applications';
import { useSidebar } from './sidebar-context';
import { WORKSPACE_NAVIGATION, type NavItem, type WorkspaceClientId } from './nav.config';

function NavIcon({ path, className }: { path: string; className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.6}
      stroke="currentColor"
      className={clsx('h-4 w-4 shrink-0', className)}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

function CollapseIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      className={clsx('h-4 w-4 transition-transform', collapsed ? 'rotate-180' : '')}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
    </svg>
  );
}

function GroupChevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" className={clsx('h-4 w-4 transition-transform', open ? 'rotate-180' : '')} aria-hidden="true">
      <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WorkspaceSidebarItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        clsx(
          'flex items-center rounded-md px-3 py-2.5 text-sm font-semibold no-underline transition-colors',
          collapsed ? 'justify-center gap-0' : 'gap-3',
          isActive
            ? 'bg-[#243B8F] text-white shadow-sm shadow-black/10'
            : 'text-[#B7C4D8] hover:bg-white/8 hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          <NavIcon path={item.icon} className={isActive ? 'text-white' : 'text-[#7E91B4]'} />
          <span className={clsx('truncate', collapsed ? 'sr-only' : '')}>{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

export function WorkspaceSidebar() {
  const { pathname } = useLocation();
  const { collapsed, toggle } = useSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const { access } = useAdminConsoleAccess();
  const { currentApplication } = useWorkspaceApplications();
  const currentNav = WORKSPACE_NAVIGATION[currentApplication.clientId as WorkspaceClientId] ?? WORKSPACE_NAVIGATION['admin-bff'];
  const grantedPermissions = currentApplication.permissions.map((permission) => permission.trim().toUpperCase());
  const hasOwnerAccess = grantedPermissions.includes('OWNER_ACCESS');

  const visibleGroups = currentNav.groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          (!item.section || access[item.section]) &&
          (hasOwnerAccess ||
            !item.requiredPermissions ||
            item.requiredPermissions.every((permission) => grantedPermissions.includes(permission.trim().toUpperCase()))),
      ),
    }))
    .filter((group) => group.items.length > 0);

  const groupKey = (label: string | undefined, index: number) => label ?? `group-${index}`;
  const itemMatchesPath = (item: NavItem) => pathname === item.to || (!item.end && pathname.startsWith(`${item.to}/`));

  useEffect(() => setMobileOpen(false), [pathname]);

  useEffect(() => {
    const activeIndex = visibleGroups.findIndex((group) => group.items.some(itemMatchesPath));
    const nextOpen = new Set<string>();
    if (visibleGroups[0]) nextOpen.add(groupKey(visibleGroups[0].label, 0));
    if (activeIndex >= 0) nextOpen.add(groupKey(visibleGroups[activeIndex].label, activeIndex));
    setOpenGroups(nextOpen);
  }, [currentApplication.clientId, pathname]);

  if (visibleGroups.length === 0) {
    return null;
  }

  return (
    <aside
      className={clsx(
        'flex w-full shrink-0 flex-col border-b border-[#2D4599] bg-[#162663] text-[#B7C4D8] transition-[width] duration-200 lg:h-[calc(100vh_-_var(--header-height))] lg:self-stretch lg:overflow-hidden lg:border-b-0 lg:border-r',
        collapsed ? 'lg:w-20' : 'lg:w-64 xl:w-72',
      )}
    >
      <div className={clsx('shrink-0 border-b border-white/10 px-5 py-4', collapsed ? 'lg:px-3' : '')}>
        <div className={clsx('flex items-start justify-between gap-3', collapsed ? 'lg:justify-center' : '')}>
          <div className={clsx('min-w-0', collapsed ? 'lg:hidden' : '')}>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#41DCCB]">Workspace</p>
            <h2 className="mt-2 text-lg font-semibold text-white">{currentNav.label}</h2>
            <p className="mt-1 text-sm text-[#9EADCB]">Application navigation.</p>
          </div>

          <button
            type="button"
            onClick={toggle}
            className="platform-shell-button hidden h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/5 p-0 text-[#B7C4D8] transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 lg:flex"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            <CollapseIcon collapsed={collapsed} />
          </button>
          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            className="platform-shell-button flex h-9 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/5 px-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 lg:hidden"
            aria-expanded={mobileOpen}
            aria-controls="workspace-navigation"
          >
            {mobileOpen ? 'Close' : 'Menu'}
          </button>
        </div>
      </div>

      <nav
        id="workspace-navigation"
        className={clsx(
          'min-h-0 flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden px-3 py-4 lg:flex',
          mobileOpen ? 'flex max-h-[min(65vh,32rem)]' : 'hidden',
          collapsed ? 'lg:px-3' : '',
        )}
        aria-label={`${currentNav.label} navigation`}
      >
        {visibleGroups.map((group, index) => {
          const key = groupKey(group.label, index);
          const isOpen = openGroups.has(key);
          return (
          <div key={key} className="space-y-1">
            {group.label ? (
              <button
                type="button"
                className={clsx('platform-shell-button flex w-full items-center justify-between rounded px-3 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.2em] text-[#7E91B4] hover:bg-white/5 hover:text-white', collapsed ? 'lg:hidden' : '')}
                aria-expanded={isOpen}
                aria-controls={`workspace-nav-group-${index}`}
                onClick={() => setOpenGroups((current) => {
                  const next = new Set(current);
                  if (next.has(key)) next.delete(key); else next.add(key);
                  return next;
                })}
              >
                <span>{group.label}</span>
                <GroupChevron open={isOpen} />
              </button>
            ) : null}
            <div id={`workspace-nav-group-${index}`} className={clsx('space-y-1', isOpen ? 'block' : 'hidden')}>
            {group.items.map((item) => (
              <WorkspaceSidebarItem key={item.to} item={item} collapsed={collapsed} />
            ))}
            </div>
          </div>
          );
        })}
      </nav>
    </aside>
  );
}
